import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AIExhibitShell from "./AIExhibitShell";
import {
  playAIExhibitFixedNarration,
  stopAIExhibitFixedNarration,
} from "./audio/useAIExhibitFixedNarration";
import { getAIExhibitCopy } from "./content/aiExhibitContent";
import { getAIExhibitScreen } from "./aiExhibitScreenRegistry";

const IDLE_RESET_MS = 2 * 60 * 1000;

const ENTRY_NARRATION_BY_SCREEN_ID = Object.freeze({
  ai_family: "ai_family_ai",
  ai_today: "ai_today_reasoning",
  history: "history_1950_turing",
  history_drivers: "history_drivers_all_available",
  humans_ai: "humans_ai_medicine",
  llm_assistant: "assistant_task_math",
  people_society: "people_society_education",
  planet: "planet_factor_model",
});

function createVisitorSessionId() {
  return `ai_explained_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function createPublicVisitorUserId(sessionId) {
  // Email-shaped on purpose so this remains compatible if user_id validation
  // becomes stricter later. It is not a Student account and logging safely
  // skips when no Student row exists.
  return `${sessionId}@kiosk.saras-3d.local`;
}

export default function AIExhibitApp({ authenticatedUserId = null }) {
  const [screenId, setScreenId] = useState("attract");
  const [history, setHistory] = useState([]);
  const [language, setLanguage] = useState("en");
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [visitorSessionId, setVisitorSessionId] = useState(() =>
    createVisitorSessionId()
  );
  const [askConversationStarted, setAskConversationStarted] = useState(false);
  const idleTimerRef = useRef(null);

  const copy = useMemo(() => getAIExhibitCopy(language), [language]);
  const visitorUserId = useMemo(() => {
    const normalizedAuthenticatedUserId =
      typeof authenticatedUserId === "string" ? authenticatedUserId.trim() : "";

    return (
      normalizedAuthenticatedUserId ||
      createPublicVisitorUserId(visitorSessionId)
    );
  }, [authenticatedUserId, visitorSessionId]);

  const resetToAttract = useCallback(() => {
    stopAIExhibitFixedNarration();
    setHistory([]);
    setScreenId("attract");
    setAudioEnabled(false);

    // A walk-up visitor must never inherit the prior visitor's AI thread.
    setVisitorSessionId(createVisitorSessionId());
    setAskConversationStarted(false);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const scheduleReset = () => {
      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
      }

      if (screenId !== "attract") {
        idleTimerRef.current = window.setTimeout(
          resetToAttract,
          IDLE_RESET_MS
        );
      }
    };

    const activityEvents = ["pointerdown", "keydown", "touchstart"];

    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, scheduleReset, { passive: true })
    );

    scheduleReset();

    return () => {
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, scheduleReset)
      );

      if (idleTimerRef.current) {
        window.clearTimeout(idleTimerRef.current);
      }
    };
  }, [screenId, resetToAttract]);

  const prepareNarrationForScreen = useCallback(
    (nextScreenId) => {
      stopAIExhibitFixedNarration();

      const narrationId = ENTRY_NARRATION_BY_SCREEN_ID[nextScreenId];

      if (!audioEnabled || !narrationId) {
        return;
      }

      // Start the destination narration while still inside the visitor's
      // navigation press so browser media playback remains user-initiated.
      playAIExhibitFixedNarration({
        audioEnabled: true,
        language,
        narrationId,
      });
    },
    [audioEnabled, language]
  );

  const navigate = useCallback(
    (nextScreenId, options = {}) => {
      if (!nextScreenId || nextScreenId === screenId) return;

      const nextScreen = getAIExhibitScreen(nextScreenId);
      if (!nextScreen) return;

      prepareNarrationForScreen(nextScreenId);

      if (options.replace) {
        setHistory([]);
      } else {
        setHistory((current) => [...current, screenId]);
      }

      setScreenId(nextScreenId);
    },
    [prepareNarrationForScreen, screenId]
  );

  const goBack = useCallback(() => {
    if (!history.length) return;

    const next = [...history];
    const previousScreen = next.pop() || "home";

    prepareNarrationForScreen(previousScreen);
    setHistory(next);
    setScreenId(previousScreen);
  }, [history, prepareNarrationForScreen]);

  const goHome = useCallback(() => {
    stopAIExhibitFixedNarration();
    setHistory([]);
    setScreenId("home");
  }, []);

  const goSources = useCallback(() => {
    if (screenId === "sources") return;

    stopAIExhibitFixedNarration();
    setHistory((current) => [...current, screenId]);
    setScreenId("sources");
  }, [screenId]);

  const toggleLanguage = useCallback(() => {
    stopAIExhibitFixedNarration();
    setLanguage((current) => (current === "en" ? "es" : "en"));
  }, []);

  const toggleAudio = useCallback(() => {
    setAudioEnabled((current) => {
      if (current) {
        stopAIExhibitFixedNarration();
      }

      return !current;
    });
  }, []);

  useEffect(
    () => () => {
      stopAIExhibitFixedNarration();
    },
    []
  );

  const screenConfig = getAIExhibitScreen(screenId);
  const ScreenComponent = screenConfig.component;

  return (
    <AIExhibitShell
      screenId={screenId}
      copy={copy}
      canGoBack={history.length > 0}
      onBack={goBack}
      onHome={goHome}
      onSources={goSources}
      onToggleLanguage={toggleLanguage}
      audioEnabled={audioEnabled}
      onToggleAudio={toggleAudio}
    >
      <ScreenComponent
        navigate={navigate}
        language={language}
        copy={copy}
        audioEnabled={audioEnabled}
        visitorUserId={visitorUserId}
        clientSessionId={visitorSessionId}
        askConversationStarted={askConversationStarted}
        onAskConversationStarted={() => setAskConversationStarted(true)}
      />
    </AIExhibitShell>
  );
}
